"use client";

import { useState } from "react";
import {
  DataTable,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  COMPOUNDING_KEYS,
  CurrencyField,
  frequencyOptions,
  perYearOf,
  useMoney,
  usePercent,
} from "@/components/tools/finance/shared";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  compareSimpleCompound,
  fromYears,
  type SimpleResult,
  type SimpleSolve,
  solveSimple,
  type TimeUnit,
  toYears,
} from "@/lib/tool-kit/finance/simple";

/** English strings. A translated content file overrides these through `ui`. */
export const SIMPLE_INTEREST_CALCULATOR_STRINGS = {
  inputsHeading: "Your numbers",
  solveFor: "Solve for",
  solveInterest: "Interest",
  solvePrincipal: "Principal",
  solveRate: "Rate",
  solveTime: "Time",
  currency: "Currency",
  plainNumbers: "No currency (plain numbers)",
  principal: "Principal (P)",
  rate: "Annual interest rate (r, %)",
  time: "Time (t)",
  interest: "Interest (I)",
  timeUnit: "Time is in",
  unitYears: "Years",
  unitMonths: "Months",
  unitDays: "Days",
  dayBasis: "Days in a year",
  basis365: "365 days",
  basis360: "360 days (banker's year)",
  resultsHeading: "Results",
  totalAmount: "Total amount (P + I)",
  ratePerYear: "Rate per year",
  timeInYears: "Time in years",
  workingHeading: "The working",
  workInterest: "I = P × r × t = {p} × {r} × {t} = {i}",
  workPrincipal: "P = I ÷ (r × t) = {i} ÷ ({r} × {t}) = {p}",
  workRate: "r = I ÷ (P × t) = {i} ÷ ({p} × {t}) = {r} = {rPct}%",
  workTime: "t = I ÷ (P × r) = {i} ÷ ({p} × {r}) = {t} years",
  workConvertMonths: "t = {value} months ÷ 12 = {t} years",
  workConvertDays: "t = {value} days ÷ {basis} = {t} years",
  workAmount: "A = P + I = {p} + {i} = {a}",
  timeEquivalent: "{years} years = {months} months = {days} days",
  compareHeading: "Simple vs compound interest",
  compareHint:
    "The same principal and rate, with interest added to the balance and earning interest in the compound column.",
  compounding: "Compound column compounds",
  yearColumn: "Years",
  simpleColumn: "Simple balance",
  compoundColumn: "Compound balance",
  differenceColumn: "Compound is higher by",
  annually: "Annually (1 per year)",
  semiannually: "Semiannually (2 per year)",
  quarterly: "Quarterly (4 per year)",
  bimonthly: "Every two months (6 per year)",
  monthly: "Monthly (12 per year)",
  semimonthly: "Twice a month (24 per year)",
  biweekly: "Every two weeks (26 per year)",
  weekly: "Weekly (52 per year)",
  daily: "Daily (365 per year)",
  continuous: "Continuously",
  needInputs:
    "Enter the three known values, each greater than zero, to see the result.",
} as const;

type Strings = typeof SIMPLE_INTEREST_CALCULATOR_STRINGS;

interface Form {
  mode: SimpleSolve;
  principal: string;
  rate: string;
  time: string;
  interest: string;
  unit: TimeUnit;
  basis: string;
}

function solveForm(form: Form): SimpleResult | null {
  const basis = Number(form.basis);
  const time = parseNumberInput(form.time);
  const rate = parseNumberInput(form.rate);
  return solveSimple(form.mode, {
    principal: parseNumberInput(form.principal),
    rate: rate === null ? null : rate / 100,
    years: time === null ? null : toYears(time, form.unit, basis),
    interest: parseNumberInput(form.interest),
  });
}

function WorkingLines({
  result,
  form,
  strings,
  currency,
}: {
  result: SimpleResult;
  form: Form;
  strings: Strings;
  currency: string;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const percent = usePercent();
  const num = (v: number) => fmt(v, { maximumFractionDigits: 6 });
  const basis = Number(form.basis);
  const values = {
    p: money(result.principal),
    i: money(result.interest),
    a: money(result.amount),
    r: num(result.rate),
    rPct: percent(result.rate, 6),
    t: num(result.years),
    value: form.time,
    basis: fmt(basis),
    years: num(result.years),
    months: num(result.years * 12),
    days: num(result.years * basis),
  };
  const lines: Record<SimpleSolve, string> = {
    interest: strings.workInterest,
    principal: strings.workPrincipal,
    rate: strings.workRate,
    time: strings.workTime,
  };
  const converted: string[] = [];
  if (form.mode !== "time" && form.unit === "months") {
    converted.push(t(strings.workConvertMonths, values));
  }
  if (form.mode !== "time" && form.unit === "days") {
    converted.push(t(strings.workConvertDays, values));
  }
  if (form.mode === "time") {
    converted.push(t(strings.timeEquivalent, values));
  }
  const all = [
    ...converted,
    t(lines[form.mode], values),
    t(strings.workAmount, values),
  ];
  return (
    <div className={`${CARD} space-y-1`}>
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      {all.map((line) => (
        <p
          className="font-mono text-muted-foreground text-sm leading-relaxed"
          key={line}
        >
          {line}
        </p>
      ))}
    </div>
  );
}

function Comparison({
  result,
  currency,
  strings,
}: {
  result: SimpleResult;
  currency: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const [compounding, setCompounding] = useState("1");
  const rows = compareSimpleCompound(
    result.principal,
    result.rate,
    result.years,
    perYearOf(compounding)
  );
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.compareHeading}</h3>
      <p className={HINT}>{strings.compareHint}</p>
      <div className="max-w-sm">
        <SelectField
          id="si-compounding"
          label={strings.compounding}
          onChange={setCompounding}
          options={frequencyOptions(strings, COMPOUNDING_KEYS, true)}
          value={compounding}
        />
      </div>
      <DataTable
        head={[
          strings.yearColumn,
          strings.simpleColumn,
          strings.compoundColumn,
          strings.differenceColumn,
        ]}
        rows={rows.map((row) => [
          fmt(row.years, { maximumFractionDigits: 4 }),
          money(row.simple),
          money(row.compound),
          money(row.compound - row.simple),
        ])}
      />
    </section>
  );
}

function Results({
  result,
  form,
  currency,
  strings,
}: {
  result: SimpleResult;
  form: Form;
  currency: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const percent = usePercent();
  const basis = Number(form.basis);
  const shown = fromYears(result.years, form.unit, basis);
  const solved: Record<SimpleSolve, string> = {
    interest: money(result.interest),
    principal: money(result.principal),
    rate: `${percent(result.rate, 4)}%`,
    time: fmt(shown, { maximumFractionDigits: 4 }),
  };
  const unitNames: Record<TimeUnit, string> = {
    years: strings.unitYears,
    months: strings.unitMonths,
    days: strings.unitDays,
  };
  const labels: Record<SimpleSolve, string> = {
    interest: strings.interest,
    principal: strings.principal,
    rate: strings.ratePerYear,
    time: `${strings.time} — ${unitNames[form.unit]}`,
  };
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={labels[form.mode]}
          value={solved[form.mode]}
        />
        <StatCard label={strings.totalAmount} value={money(result.amount)} />
        <StatCard
          label={strings.timeInYears}
          value={fmt(result.years, { maximumFractionDigits: 4 })}
        />
      </StatGrid>
      <WorkingLines
        currency={currency}
        form={form}
        result={result}
        strings={strings}
      />
      <Comparison currency={currency} result={result} strings={strings} />
    </section>
  );
}

export default function SimpleInterestCalculatorWidget() {
  const strings = useToolUi(SIMPLE_INTEREST_CALCULATOR_STRINGS);
  const [currency, setCurrency] = useState("USD");
  const [form, setForm] = useState<Form>({
    mode: "interest",
    principal: "5000",
    rate: "4.5",
    time: "3",
    interest: "675",
    unit: "years",
    basis: "365",
  });
  const update = (patch: Partial<Form>) =>
    setForm((old) => ({ ...old, ...patch }));
  const result = solveForm(form);
  const { mode } = form;

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <PillGroup
          label={strings.solveFor}
          name="si-mode"
          onChange={(value) => update({ mode: value })}
          options={[
            { value: "interest", label: strings.solveInterest },
            { value: "principal", label: strings.solvePrincipal },
            { value: "rate", label: strings.solveRate },
            { value: "time", label: strings.solveTime },
          ]}
          value={mode}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <CurrencyField
            id="si-currency"
            label={strings.currency}
            onChange={setCurrency}
            plainLabel={strings.plainNumbers}
            value={currency}
          />
          {mode === "principal" ? null : (
            <TextField
              id="si-principal"
              label={strings.principal}
              numeric
              onChange={(value) => update({ principal: value })}
              value={form.principal}
            />
          )}
          {mode === "rate" ? null : (
            <TextField
              id="si-rate"
              label={strings.rate}
              numeric
              onChange={(value) => update({ rate: value })}
              value={form.rate}
            />
          )}
          {mode === "time" ? null : (
            <TextField
              id="si-time"
              label={strings.time}
              numeric
              onChange={(value) => update({ time: value })}
              value={form.time}
            />
          )}
          {mode === "interest" ? null : (
            <TextField
              id="si-interest"
              label={strings.interest}
              numeric
              onChange={(value) => update({ interest: value })}
              value={form.interest}
            />
          )}
          <SelectField
            id="si-unit"
            label={strings.timeUnit}
            onChange={(value) => update({ unit: value })}
            options={[
              { value: "years", label: strings.unitYears },
              { value: "months", label: strings.unitMonths },
              { value: "days", label: strings.unitDays },
            ]}
            value={form.unit}
          />
          {form.unit === "days" || mode === "time" ? (
            <SelectField
              id="si-basis"
              label={strings.dayBasis}
              onChange={(value) => update({ basis: value })}
              options={[
                { value: "365", label: strings.basis365 },
                { value: "360", label: strings.basis360 },
              ]}
              value={form.basis}
            />
          ) : null}
        </div>
      </section>
      {result ? (
        <Results
          currency={currency}
          form={form}
          result={result}
          strings={strings}
        />
      ) : (
        <Notice>{strings.needInputs}</Notice>
      )}
    </div>
  );
}
